import React, { useMemo, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, Download } from 'lucide-react';
import {
  getCompanyDisplayName,
  getContactFullName,
  getContactContextLabel,
} from '../utils/companyDisplayName';

/**
 * ContactVCardQR — Fase 1 (Contatos 1.1 + 1.5).
 *
 * Modal com QR Code vCard 3.0 do contato + botão de download .vcf.
 * Escaneando com a câmera do celular, o app de contatos abre direto.
 *
 * Convenção (1.5): FN usa o padrão "EMPRESA | Nome Completo" para que o
 * contato apareça agrupado na agenda do celular. N (nome estruturado) fica
 * separado para sorting nativo do app de contatos.
 */

// Escapa caracteres especiais do vCard 3.0: \ ; , \n
const escapeVcard = (s) =>
  String(s ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');

export const buildVCard = (contact, company) => {
  if (!contact) return '';
  const first = (contact.first_name || '').trim();
  const last = (contact.last_name || '').trim();
  const displayName = getCompanyDisplayName(company);
  const fullName = getContactFullName(contact);
  const contextLabel = getContactContextLabel(company, contact);
  const lines = ['BEGIN:VCARD', 'VERSION:3.0'];

  // N: nome estruturado preserva sorting nativo do app de contatos
  if (last || first) lines.push(`N:${escapeVcard(last)};${escapeVcard(first)};;;`);
  // FN: rótulo de exibição "EMPRESA | Nome" (fallback para nome ou empresa só)
  if (contextLabel) lines.push(`FN:${escapeVcard(contextLabel)}`);
  if (displayName) lines.push(`ORG:${escapeVcard(displayName)}`);
  if (contact.title) lines.push(`TITLE:${escapeVcard(contact.title)}`);
  if (contact.mobile) lines.push(`TEL;TYPE=CELL:${contact.mobile}`);
  if (contact.phone) lines.push(`TEL;TYPE=WORK:${contact.phone}`);
  if (contact.email) lines.push(`EMAIL;TYPE=WORK:${contact.email}`);
  if (contact.person_linkedin_url) lines.push(`URL:${contact.person_linkedin_url}`);
  lines.push(`NOTE:${escapeVcard(contextLabel || fullName || displayName)} — CRM Aston Capital`);
  lines.push('END:VCARD');

  return lines.join('\r\n');
};

const sanitizeFilename = (s) =>
  String(s || 'contato')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .replace(/_+/g, '_');

const downloadVcf = (vcardText, displayName, firstName, lastName) => {
  const parts = [displayName, firstName, lastName]
    .map((p) => sanitizeFilename(p || ''))
    .filter((p) => p && p !== '_');
  const filename = `${parts.join('_') || 'contato'}.vcf`;
  const blob = new Blob([vcardText], { type: 'text/vcard;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const ContactVCardQR = ({ contact, company, onClose }) => {
  const vcardText = useMemo(() => buildVCard(contact, company), [contact, company]);
  const displayName = getCompanyDisplayName(company);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  if (!contact) return null;

  const fullName = getContactFullName(contact) || 'Contato';
  const headerLabel = getContactContextLabel(company, contact) || fullName;

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`QR Code vCard de ${headerLabel}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-800 truncate" title={headerLabel}>{headerLabel}</h3>
            {(contact.title || displayName) && (
              <p className="text-[11px] text-slate-500 truncate" title={[contact.title, displayName].filter(Boolean).join(' · ')}>
                {[contact.title, displayName].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="p-1 rounded hover:bg-slate-200 text-slate-500"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 flex flex-col items-center gap-3">
          <div className="bg-white p-2 border border-slate-200 rounded">
            <QRCodeSVG value={vcardText} size={200} level="M" includeMargin={false} />
          </div>
          <p className="text-[11px] text-slate-500 text-center">
            Aponte a câmera do celular para adicionar este contato à agenda.
          </p>
          <button
            type="button"
            onClick={() => downloadVcf(vcardText, displayName, contact.first_name, contact.last_name)}
            className="mt-1 px-3 py-1.5 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700 flex items-center gap-1.5"
          >
            <Download size={12} /> Baixar .vcf
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactVCardQR;
